import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { getProgramLogoUrl } from "@/config/programBranding";
import { useIsMobile } from "@/hooks/use-mobile";
import { Search, Briefcase, ArrowLeft, Check, X } from "lucide-react";

const STORAGE_KEY = "ekselencia_visitor_segment";

type VisitorType = "seeker" | "professional";

interface Category {
  id: string;
  emoji: string;
  label: string;
  shortLabel: string;
  seekerPath: string;
  professionalPath: string;
}

const CATEGORIES: Category[] = [
  { id: "trainer", emoji: "🏋️", label: "Rendix - Entrenadores", shortLabel: "Entrenadores", seekerPath: "/trainers", professionalPath: "/become-trainer" },
  { id: "physio", emoji: "💆", label: "FisioAxis - Fisioterapeutas", shortLabel: "Fisios", seekerPath: "/physiotherapists", professionalPath: "/become-physiotherapist" },
  { id: "nutritionist", emoji: "🥗", label: "NutriCore - Nutricionistas", shortLabel: "Nutris", seekerPath: "/nutritionists", professionalPath: "/become-nutritionist" },
  { id: "yoga", emoji: "🧘", label: "Asanex - Estudios de Yoga", shortLabel: "Yoga", seekerPath: "/landing-yoga", professionalPath: "/funnel/yoga" },
  { id: "crossfit", emoji: "💪", label: "Crossnity - CrossFit", shortLabel: "CrossFit", seekerPath: "/landing-crossfit", professionalPath: "/funnel/crossfit" },
  { id: "gym", emoji: "🏢", label: "Gymnex - Gimnasios", shortLabel: "Gimnasios", seekerPath: "/landing-fitness", professionalPath: "/funnel/gym" },
  { id: "pilates", emoji: "🤸", label: "Corevia - Pilates", shortLabel: "Pilates", seekerPath: "/landing-pilates", professionalPath: "/funnel/pilates" },
  { id: "dance", emoji: "💃", label: "CoreoHub - Baile", shortLabel: "Baile", seekerPath: "/landing-dance", professionalPath: "/funnel/dance" },
  { id: "martial", emoji: "🥋", label: "Budotrax - Artes Marciales", shortLabel: "Artes Marciales", seekerPath: "/landing-martial-arts", professionalPath: "/funnel/martial-arts" },
];

export const VisitorSegmentationModal = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [visitorType, setVisitorType] = useState<VisitorType | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      const timer = setTimeout(() => setOpen(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleVisitorTypeSelect = (type: VisitorType) => {
    setVisitorType(type);
    setStep(2);
  };

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };

  const handleConfirm = () => {
    if (!visitorType || !selectedCategory) return;

    const category = CATEGORIES.find(c => c.id === selectedCategory);
    if (!category) return;

    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      type: visitorType,
      category: selectedCategory,
      timestamp: Date.now()
    }));

    setOpen(false);

    const path = visitorType === "seeker" ? category.seekerPath : category.professionalPath;
    navigate(path);
  };

  const handleSkip = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      type: "skipped",
      timestamp: Date.now()
    }));
    setOpen(false);
  };

  const ContentComponent = () => (
    <div className="flex flex-col h-full">
      {step === 1 ? (
        <div className="flex flex-col gap-3 sm:gap-4 py-2 sm:py-4">
          {/* Seeker Option */}
          <button
            onClick={() => handleVisitorTypeSelect("seeker")}
            className={cn(
              "relative w-full p-4 sm:p-6 rounded-xl sm:rounded-2xl border-2 transition-all duration-300",
              "bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5",
              "border-primary/20 hover:border-primary hover:shadow-lg hover:shadow-primary/10",
              "flex flex-col items-center gap-2 sm:gap-3 text-center group"
            )}
          >
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Search className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold text-foreground">Estoy buscando...</h3>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Encontrar profesionales, gimnasios o centros
              </p>
            </div>
          </button>

          {/* Professional Option */}
          <button
            onClick={() => handleVisitorTypeSelect("professional")}
            className={cn(
              "relative w-full p-4 sm:p-6 rounded-xl sm:rounded-2xl border-2 transition-all duration-300",
              "bg-gradient-to-br from-accent/5 via-accent/10 to-accent/5",
              "border-accent/20 hover:border-accent hover:shadow-lg hover:shadow-accent/10",
              "flex flex-col items-center gap-2 sm:gap-3 text-center group"
            )}
          >
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-accent/10 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Briefcase className="w-6 h-6 sm:w-8 sm:h-8 text-accent" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold text-foreground">Soy profesional</h3>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Promocionar mis servicios y captar clientes
              </p>
            </div>
          </button>
        </div>
      ) : (
        <div className="flex flex-col h-full">
          {/* Category Grid - Scrollable */}
          <div className="flex-1 overflow-y-auto py-2 -mx-2 px-2">
            <RadioGroup
              value={selectedCategory || ""}
              onValueChange={handleCategorySelect}
              className="grid grid-cols-1 sm:grid-cols-2 gap-2"
            >
              {CATEGORIES.map((category) => {
                const logoUrl = getProgramLogoUrl(category.id);
                const isSelected = selectedCategory === category.id;
                
                return (
                  <Label
                    key={category.id}
                    htmlFor={category.id}
                    className={cn(
                      "relative flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg sm:rounded-xl border-2 cursor-pointer transition-all duration-200",
                      isSelected
                        ? "border-primary bg-primary/10 shadow-md"
                        : "border-border/50 hover:border-primary/50 hover:bg-muted/30"
                    )}
                  >
                    <RadioGroupItem 
                      value={category.id} 
                      id={category.id} 
                      className="sr-only"
                    />
                    
                    {/* Icon/Logo */}
                    <div className={cn(
                      "w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0",
                      isSelected ? "bg-primary/20" : "bg-muted/50"
                    )}>
                      {logoUrl ? (
                        <img 
                          src={logoUrl} 
                          alt={category.label} 
                          className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
                        />
                      ) : (
                        <span className="text-xl sm:text-2xl">{category.emoji}</span>
                      )}
                    </div>
                    
                    {/* Label */}
                    <span className={cn(
                      "font-medium text-sm sm:text-base flex-1 text-left",
                      isSelected ? "text-primary" : "text-foreground"
                    )}>
                      <span className="hidden sm:inline">{category.label}</span>
                      <span className="sm:hidden">{category.shortLabel}</span>
                    </span>
                    
                    {/* Check indicator */}
                    {isSelected && (
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 sm:w-4 sm:h-4 text-primary-foreground" />
                      </div>
                    )}
                  </Label>
                );
              })}
            </RadioGroup>
          </div>

          {/* Actions - Fixed at bottom */}
          <div className="flex gap-2 sm:gap-3 pt-4 mt-auto border-t">
            <Button
              variant="outline"
              onClick={() => setStep(1)}
              className="flex-1 h-10 sm:h-11"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              Atrás
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!selectedCategory}
              className="flex-1 h-10 sm:h-11"
            >
              Continuar
            </Button>
          </div>
        </div>
      )}

      {/* Skip link */}
      <button
        onClick={handleSkip}
        className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground text-center w-full mt-3 sm:mt-4 py-2 transition-colors"
      >
        <X className="w-3 h-3" />
        Omitir por ahora
      </button>
    </div>
  );

  // Use Drawer on mobile, Dialog on desktop
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={(isOpen) => {
        if (!isOpen) handleSkip();
      }}>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader className="text-center pb-2">
            <DrawerTitle className="text-xl">
              {step === 1 ? "¡Bienvenido a Ekselencia!" : "¿Qué te interesa?"}
            </DrawerTitle>
            <DrawerDescription className="text-sm">
              {step === 1 
                ? "Cuéntanos quién eres para personalizar tu experiencia"
                : visitorType === "seeker" 
                  ? "Selecciona qué tipo de profesional o centro buscas"
                  : "Selecciona tu tipo de negocio o servicio"
              }
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-6 overflow-y-auto">
            <ContentComponent />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) handleSkip();
    }}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-2xl text-center">
            {step === 1 ? "¡Bienvenido a Ekselencia!" : "¿Qué te interesa?"}
          </DialogTitle>
          <DialogDescription className="text-center text-base">
            {step === 1 
              ? "Cuéntanos quién eres para personalizar tu experiencia"
              : visitorType === "seeker" 
                ? "Selecciona qué tipo de profesional o centro buscas"
                : "Selecciona tu tipo de negocio o servicio"
            }
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto">
          <ContentComponent />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VisitorSegmentationModal;
